import React, { useState, useCallback, useRef, useReducer, useMemo, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useBlocker } from 'react-router-dom';

import { useDispatch, useSelector } from '@tmapy/redux';
import { useMessage, useSelectMessages } from '@tmapy/intl';

import './PublishsetForm.scss';

import type { CallApiResult } from '@tmapy/beetlejuice-utils';
import { HTTP_STATUS_OK } from '@tmapy/beetlejuice-utils';
import type { MultiSelectProps } from '@tmapy/style-guide';
import {
  BtnBar,
  Checkbox,
  CtaBtn,
  DangerAlert,
  Form,
  FormItem,
  FormRow,
  CheckboxGroup,
  Input,
  MultiSelect,
  RadioBtnGroup,
  RadioGroup,
  SecondaryBtn,
  Select,
  SuccessAlert,
  Textarea,
  WaitingAlert,
} from '@tmapy/style-guide';

import type { OmittedFormItemProps, PublishsetFormat, ServerErrors } from '../../types';
import type { ExportToolResponse } from '../../openapi/types/ExportTool';
import { QUERY_STATE } from '../../constants';
import { getFormValues } from '../../docs/utils/getformvalues';
import { actionResourceInvalidate } from '../../redux/resource/actions';
import { FormBlock } from '../../app/components/FormBlock';
import { MainContent } from '../../app/components/MainContent';
import { PageTitle } from '../../app/components/PageTitle';
import {
  PUBLISHSET_LIST_FILTER,
  TAGS_LIST_FILTER,
} from '../../orders/components/OrderPublishsetList';

import type { InputProps } from '../../hooks/useInput';
import type { UseResource } from '../../hooks/useResource';
import {
  useInput,
  useAuth,
  useCheckbox,
  useHistoryAction,
  useMultiSelect,
  useFormItemServerErrors,
  useSelect,
} from '../../hooks';

import type {
  PublishsetUpdateRequest,
  PublishsetUpdateResponse,
  PublishsetRetrieveResponse,
} from '../../openapi/types';
import { DatatypeEnum } from '../../openapi/types/DatatypeEnum';
import { DtmDataTypeEnum } from '../../openapi/types/DtmDataTypeEnum';
import { useApproverRolesListResource } from '../../openapi/operations/approverRolesList';
import { useFormatsListResource } from '../../openapi/operations/formatsList';
import { useTileindexesListResource } from '../../openapi/operations/tileindexesList';
import { priceListItemsList } from '../../openapi/operations/priceListItemsList';
import { priceListItemsRetrieveOperation } from '../../openapi/operations/priceListItemsRetrieve';
import { DATA_PRICE_LIST_ITEM_TYPES } from '../../priceList/utils';
import { useExportToolsListResource } from '../../openapi/operations/exportToolsList';
import { useGpTasksListResource } from '../../openapi/operations/gpTasksList';
import { publishsetsListOperation } from '../../openapi/operations/publishsetsList';
import {
  publishsetTagsList,
  publishsetTagsListOperation,
} from '../../openapi/operations/publishsetTagsList';
import { publishsetsAttachmentsList } from '../../openapi/operations/publishsetsAttachmentsList';

import { commonMessages } from '../../commonMessages';

import { PUBLISHSET_FORM_TYPE, PUBLISHSET_STATES, PUBLISHSET_TYPES } from '../constants';

import {
  publishsetFormMessages,
  publishsetTitleMessages,
  publishsetQueryMessages,
  publishsetFailMessages,
  publishsetBtnMessages,
  publishsetStateMessages,
  publishsetTypesLabelMessages,
  publishsetDatatypeMessages,
  publishsetDtmDataTypeMessages,
} from './publishsetMessages';
import { PublishsetDatasetItem, PublishsetDatasetListItem } from './PublishsetDatasetListItem';
import { FormatTypeBadge } from './FormatTypeBadge';
import { FormatCard } from './FormatCard';

type PublishsetFormProps = {
  formType: PUBLISHSET_FORM_TYPE;
  resource: UseResource<PublishsetRetrieveResponse>;
  submitQueryState: QUERY_STATE;
  deleteQueryState: QUERY_STATE;
  onSubmit(value: PublishsetUpdateRequest): Promise<CallApiResult<PublishsetUpdateResponse>>;
  onDelete(): Promise<CallApiResult<unknown>>;
};

type TagItem = { name: string; url: string };

const renderItemMultiSelect = (item: TagItem) => item.name;

const getDefaultTool = (tools: ExportToolResponse[]) =>
  tools.find((tool) => tool.is_default) ?? (tools.length === 1 ? tools[0] : null);

const omitKey = (record: Record<string, string>, key: string) =>
  Object.fromEntries(Object.entries(record).filter(([recordKey]) => recordKey !== key));

const readInitialSelections = (data: PublishsetRetrieveResponse | null | undefined) => {
  const toolByFormatUrl: Record<string, string> = {};
  const tileindexByFormatUrl: Record<string, string> = {};
  for (const sel of data?.export_tool_selections ?? []) {
    // API vrací export_tool buď jako nested objekt { url: "..." } nebo jako flat URL string
    const exportTool = sel.export_tool as unknown;
    const toolUrl =
      typeof exportTool === 'string' ? exportTool : (exportTool as { url: string } | null)?.url;
    // sel.format může být název formátu (např. "DWG"), ne URL – najdeme správnou URL přes data.formats
    const formatObj = (data?.formats as Array<{ url: string; name: string }> | undefined)?.find(
      (f) => f.url === sel.format || f.name === sel.format,
    );
    const formatKey = formatObj?.url ?? sel.format;
    if (toolUrl) toolByFormatUrl[formatKey] = toolUrl;
    if (sel.tileindex?.url) tileindexByFormatUrl[formatKey] = sel.tileindex.url;
  }
  return { toolByFormatUrl, tileindexByFormatUrl };
};

const PUBLISHSET_DATASETS_ACTION_TYPE = {
  ADD_NEW_ITEM: 'ADD_NEW_ITEM',
  UPDATE_ITEM: 'UPDATE_ITEM',
  DELETE_ITEM: 'DELETE_ITEM',
  SET_SERVER_ERRORS: 'SET_SERVER_ERRORS',
} as const;

export const PublishsetForm: React.FC<PublishsetFormProps> = ({
  formType,
  resource,
  submitQueryState,
  deleteQueryState,
  onSubmit,
  onDelete,
}) => {
  const auth = useAuth();
  const formatMessage = useMessage();
  const dispatch = useDispatch();
  const loadQueryState = resource.queryState;

  const customerType = useSelector((state) => state.app.config.customerType);
  const hideTags = useSelector((state) => state.app.config.vydej?.hideTags);
  const [validate, setValidate] = useState(false);
  const [isFormChanged, setFormChanged] = useState(false);
  const [serverErrors, setServerErrors] = useState<ServerErrors>();
  const formItemServerErrors = useFormItemServerErrors(serverErrors);

  const [publishsetDatasets, dispatchPublishsetDatasetsAction] = useReducer(
    (state: PublishsetDatasetItem[], action: any): PublishsetDatasetItem[] => {
      switch (action.type) {
        case PUBLISHSET_DATASETS_ACTION_TYPE.ADD_NEW_ITEM:
          return [...state, { uid: action.uid }] as PublishsetDatasetItem[];
        case PUBLISHSET_DATASETS_ACTION_TYPE.UPDATE_ITEM:
          return state.map((publishsetDataset) =>
            publishsetDataset.uid === action.uid || publishsetDataset.id === action.uid
              ? action.newPublishsetDataset
              : publishsetDataset,
          );
        case PUBLISHSET_DATASETS_ACTION_TYPE.DELETE_ITEM:
          return state.filter(
            (publishsetDataset) =>
              publishsetDataset.uid !== action.uid && publishsetDataset.id !== action.uid,
          );
        case PUBLISHSET_DATASETS_ACTION_TYPE.SET_SERVER_ERRORS:
          return state
            .filter((publishsetDataset) => publishsetDataset.dataset)
            .map((publishsetDataset, index: number) => ({
              ...publishsetDataset,
              serverErrors: action.errors?.[index],
            }));
        default:
          return state;
      }
    },
    (resource.data?.publishset_datasets ?? []) as PublishsetDatasetItem[],
  );

  const acl = useMemo(
    () => ({
      doNotRequireFormAndType: auth.hasPermission(
        'Publishsets',
        'do_not_require_form_and_type',
        resource.data,
      ),
      updateAdminOnly:
        customerType === 'ipr' &&
        auth.hasPermission('Publishsets', 'update.publishset_admin_only', resource.data ?? null),
    }),
    [auth, resource.data, customerType],
  );
  useBlocker(() => {
    if (isFormChanged && submitQueryState !== QUERY_STATE.SUCCESS) {
      const confirmLeave = window.confirm(
        formatMessage(publishsetFormMessages.confirmLeaveMessage),
      );
      if (confirmLeave) {
        return false;
      }
      return true;
    }
    return false;
  });

  const onDatasetChange = (uid: number, newPublishsetDataset: PublishsetDatasetItem) => {
    setFormChanged(true);
    dispatchPublishsetDatasetsAction({
      type: PUBLISHSET_DATASETS_ACTION_TYPE.UPDATE_ITEM,
      uid,
      newPublishsetDataset,
    });
  };

  const onDatasetDelete = (uid: number) => {
    setFormChanged(
      !!publishsetDatasets.find(
        (publishsetDataset) => publishsetDataset?.id === uid || publishsetDataset?.uid === uid,
      )?.dataset,
    );
    dispatchPublishsetDatasetsAction({
      type: PUBLISHSET_DATASETS_ACTION_TYPE.DELETE_ITEM,
      uid,
    });
  };

  const handleNewPublishsetDataset = () => {
    dispatchPublishsetDatasetsAction({
      type: PUBLISHSET_DATASETS_ACTION_TYPE.ADD_NEW_ITEM,
      uid: -new Date().getTime(),
    });
  };

  const onInputChangeCallback = useCallback(() => {
    setFormChanged(true);
    setValidate(false);
    setServerErrors({});
  }, [setFormChanged, setValidate]);

  // INPUTS
  const name = useInput<InputProps>({
    name: 'name',
    initialValue: resource.data?.name ?? '',
    label: formatMessage(publishsetFormMessages.name),
    validate,
    validator: (value) => (value ? null : formatMessage(publishsetFormMessages.nameRequired)),
    isRequired: true,
    autoFocus: formType === PUBLISHSET_FORM_TYPE.NEW,
    onChange: onInputChangeCallback,
    ...formItemServerErrors.name,
  });

  const publishsetStateItems = useSelectMessages(publishsetStateMessages, PUBLISHSET_STATES);

  const state = useInput({
    name: 'state',
    initialValue: resource.data?.state ?? PUBLISHSET_STATES.PREPARING,
    label: formatMessage(publishsetFormMessages.state),
    items: publishsetStateItems,
    validate,
    isRequired: true,
    onChange: onInputChangeCallback,
    ...formItemServerErrors.state,
  });

  const admin_only = useCheckbox<any>({
    name: 'admin_only',
    initialValue: resource.data?.admin_only ?? false,
    onChange: onInputChangeCallback,
    ...formItemServerErrors.admin_only,
  });

  const datatypeItems = useSelectMessages(publishsetDatatypeMessages, DatatypeEnum);

  const datatype = useInput({
    name: 'datatype',
    initialValue: resource.data?.datatype ?? DatatypeEnum.STANDARD,
    label: formatMessage(publishsetFormMessages.datatype),
    items: datatypeItems,
    validate,
    isRequired: !acl.doNotRequireFormAndType,
    onChange: onInputChangeCallback,
    ...formItemServerErrors.datatype,
  });

  const dtmDataTypeItems = useSelectMessages(publishsetDtmDataTypeMessages, DtmDataTypeEnum);

  const data_type = useInput({
    name: 'data_type',
    initialValue: resource.data?.data_type ?? '',
    label: formatMessage(publishsetFormMessages.dtmDataType),
    items: dtmDataTypeItems,
    validate,
    validator: (value) =>
      !value && customerType === 'dtm'
        ? formatMessage(publishsetFormMessages.dtmDataTypeRequired)
        : null,
    isRequired: true,
    onChange: onInputChangeCallback,
    ...formItemServerErrors.data_type,
  });

  const description = useInput<InputProps>({
    name: 'description',
    initialValue: resource.data?.description ?? '',
    label: formatMessage(publishsetFormMessages.description),
    validate,
    onChange: onInputChangeCallback,
    ...formItemServerErrors.description,
  });

  const external_description_url = useInput<InputProps>({
    name: 'external_description_url',
    initialValue: resource.data?.external_description_url ?? '',
    label: formatMessage(publishsetFormMessages.externalDescriptionUrl),
    validate,
    onChange: onInputChangeCallback,
    ...formItemServerErrors.external_description_url,
  });

  const prepareUtilitiesTypesItems = useCallback(async (val: string) => {
    const response = await publishsetTagsList({ query: { name__icontains: val } });
    if (response.status === HTTP_STATUS_OK) {
      return response.data?.results;
    }
    return null;
  }, []);

  const tags = useMultiSelect<MultiSelectProps<TagItem> | OmittedFormItemProps, TagItem>({
    name: 'tags',
    label: formatMessage(publishsetFormMessages.tags),
    initialValue: resource.data?.tags as any,
    onChange: onInputChangeCallback,
    prepareItems: prepareUtilitiesTypesItems,
    renderValue: (item) => (item.name ? item.name : ''),
    renderItem: renderItemMultiSelect,
    validate,
    ...formItemServerErrors.tags,
  });

  const prepareAttachments = useCallback(async () => {
    const response = await publishsetsAttachmentsList({});
    if (response.status === HTTP_STATUS_OK) {
      return response.data?.attachments;
    }
    return null;
  }, []);

  const attachments = useMultiSelect<MultiSelectProps<string> | OmittedFormItemProps, string>({
    name: 'attachments',
    label: formatMessage(publishsetFormMessages.attachments),
    initialValue: resource.data?.attachments as any,
    onChange: onInputChangeCallback,
    prepareItems: prepareAttachments,
    renderValue: (item) => item,
    renderItem: (item) => item,
    validate,
    ...formItemServerErrors.attachments,
  });

  const availableFormats = useFormatsListResource({}, true);
  const approverRoles = useApproverRolesListResource({}, true);

  const [approver_roles, setApproverRoles] = useState<string[]>(
    resource.data?.approver_roles.map((role) => role.url) ?? [],
  );

  const [formats, setFormats] = useState<PublishsetFormat[]>(
    (resource.data?.formats ?? []) as PublishsetFormat[],
  );
  const [selectedType, setSelectedType] = useState<PUBLISHSET_TYPES>(
    resource.data?.type || formats[0]?.type || PUBLISHSET_TYPES.VECTOR,
  );
  const isTypeLocked = formType === PUBLISHSET_FORM_TYPE.EDIT && !!resource.data?.type;
  const typeItems = useSelectMessages(publishsetTypesLabelMessages, PUBLISHSET_TYPES);

  const exportToolsResource = useExportToolsListResource({}, true);
  const gpTasksResource = useGpTasksListResource({}, true);
  const tileindexesResource = useTileindexesListResource({ page_size: 1000 }, true);
  const formatsRef = useRef(formats);

  formatsRef.current = formats;

  const exportToolsByFormatUrl = useMemo(() => {
    const map: Record<string, ExportToolResponse[]> = {};
    for (const tool of exportToolsResource.data?.results ?? []) {
      if (!map[tool.format]) map[tool.format] = [];
      map[tool.format].push(tool);
    }
    return map;
  }, [exportToolsResource.data]);

  const exportToolsByUrl = useMemo(() => {
    const map: Record<string, ExportToolResponse> = {};
    for (const tool of exportToolsResource.data?.results ?? []) {
      map[tool.url] = tool;
    }
    return map;
  }, [exportToolsResource.data]);

  const tileindexItems = useMemo(
    () =>
      (tileindexesResource.data?.results ?? []).map((tileindex) => ({
        value: tileindex.url,
        label: tileindex.name,
      })),
    [tileindexesResource.data],
  );

  const gpTaskNameById = useMemo(() => {
    const map: Record<number, string> = {};
    for (const task of gpTasksResource.data?.results ?? []) {
      map[task.id] = task.name;
    }
    return map;
  }, [gpTasksResource.data]);

  const getExportToolLabel = useCallback(
    (tool: ExportToolResponse) => {
      const toolTypeName = tool.is_package
        ? formatMessage(publishsetFormMessages.exportToolPackage)
        : tool.gp_task != null
          ? (gpTaskNameById[tool.gp_task] ?? String(tool.gp_task))
          : '';
      return tool.tool_name ? `${toolTypeName} (${tool.tool_name})` : toolTypeName;
    },
    [gpTaskNameById, formatMessage],
  );

  const [exportToolSelections, setExportToolSelections] = useState<Record<string, string>>(
    () => readInitialSelections(resource.data).toolByFormatUrl,
  );
  const [tileindexSelections, setTileindexSelections] = useState<Record<string, string>>(
    () => readInitialSelections(resource.data).tileindexByFormatUrl,
  );

  // Po načtení exportních nástrojů předvyber výchozí nástroj pro formáty bez výběru.
  // formatsRef zajišťuje čtení aktuálních formats bez nutnosti přidávat je do deps
  // (přidání by způsobovalo opakované spouštění při každé změně zaškrtnutých formátů).
  useEffect(() => {
    if (!exportToolsResource.data) return;
    const byUrl = exportToolsByFormatUrl;
    setExportToolSelections((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const format of formatsRef.current) {
        if (!next[format.url]) {
          const defaultTool = getDefaultTool(byUrl[format.url] ?? []);
          if (defaultTool) {
            next[format.url] = defaultTool.url;
            changed = true;
          }
        }
      }
      return changed ? next : prev;
    });
  }, [exportToolsResource.data, exportToolsByFormatUrl]);

  const isFormatsEmpty = formats.length === 0;
  const isInvalidFormatRef = useRef(false);
  isInvalidFormatRef.current =
    !acl.doNotRequireFormAndType && isFormatsEmpty && (validate || isInvalidFormatRef.current);

  const selectedTools = formats.flatMap((format) => {
    const toolUrl = exportToolSelections[format.url];
    return toolUrl
      ? [{ format, toolUrl, isPackage: exportToolsByUrl[toolUrl]?.is_package ?? false }]
      : [];
  });

  const missingTileindexFormatUrls = selectedTools
    .filter((sel) => sel.isPackage && !tileindexSelections[sel.format.url])
    .map((sel) => sel.format.url);

  const submittedSelectionFormatUrlsRef = useRef<string[]>([]);
  const selectionServerErrorsByFormatUrl = useMemo(() => {
    const map: Record<string, ServerErrors> = {};
    const selectionErrors = serverErrors?.export_tool_selections as unknown;
    if (Array.isArray(selectionErrors)) {
      selectionErrors.forEach((error, index) => {
        const formatUrl = submittedSelectionFormatUrlsRef.current[index];
        if (formatUrl && error && typeof error === 'object' && Object.keys(error).length > 0) {
          map[formatUrl] = error as ServerErrors;
        }
      });
    }
    return map;
  }, [serverErrors]);

  const handleTypeChange = (type: PUBLISHSET_TYPES) => {
    if (type === selectedType) return;
    if (
      formats.length > 0 &&
      !window.confirm(
        formatMessage(publishsetFormMessages.typeChangeConfirm, { count: formats.length }),
      )
    ) {
      return;
    }
    onInputChangeCallback();
    setSelectedType(type);
    setFormats([]);
    setExportToolSelections({});
    setTileindexSelections({});
  };

  const handleFormatToggle = (format: PublishsetFormat, isChecked: boolean) => {
    onInputChangeCallback();
    if (isChecked) {
      setFormats((prev) => prev.filter((f) => f.url !== format.url));
      setExportToolSelections((prev) => omitKey(prev, format.url));
      setTileindexSelections((prev) => omitKey(prev, format.url));
    } else {
      setFormats((prev) => [...prev, format]);
      const defaultTool = getDefaultTool(exportToolsByFormatUrl[format.url] ?? []);
      if (defaultTool) {
        setExportToolSelections((prev) => ({ ...prev, [format.url]: defaultTool.url }));
      }
    }
  };

  const handleToolChange = (formatUrl: string, toolUrl: string) => {
    onInputChangeCallback();
    setExportToolSelections((prev) => ({ ...prev, [formatUrl]: toolUrl }));
  };

  const handleTileindexChange = (formatUrl: string, tileindexUrl: string) => {
    onInputChangeCallback();
    setTileindexSelections((prev) => ({ ...prev, [formatUrl]: tileindexUrl }));
  };

  const formatsErrorMessage = isInvalidFormatRef.current
    ? formatMessage(publishsetFormMessages.formatsRequired)
    : formItemServerErrors.formats?.errorMessage;

  const typeFormats = (availableFormats.data?.results ?? []).filter(
    (item) => item.type === selectedType,
  ) as PublishsetFormat[];
  const packageCount = selectedTools.filter((sel) => sel.isPackage).length;

  const renderFormatCards = () => (
    <>
      <div className='dmx-formatCardList'>
        {typeFormats.map((format) => {
          const selectedToolUrl = exportToolSelections[format.url] ?? '';
          return (
            <FormatCard
              key={format.url}
              format={format}
              tools={exportToolsByFormatUrl[format.url] ?? []}
              isToolsLoaded={exportToolsResource.data !== undefined}
              isChecked={formats.some((f) => f.url === format.url)}
              selectedTool={exportToolsByUrl[selectedToolUrl] ?? null}
              selectedTileindexUrl={tileindexSelections[format.url] ?? ''}
              tileindexItems={tileindexItems}
              isTileindexMissing={validate && missingTileindexFormatUrls.includes(format.url)}
              serverErrors={selectionServerErrorsByFormatUrl[format.url]}
              getToolLabel={getExportToolLabel}
              onToggle={handleFormatToggle}
              onToolChange={handleToolChange}
              onTileindexChange={handleTileindexChange}
            />
          );
        })}
      </div>
      <div className='dmx-formatCardList--summary'>
        <FormattedMessage
          {...publishsetFormMessages.formatsSummary}
          values={{ selected: formats.length, total: typeFormats.length }}
        />
        {packageCount > 0 && (
          <>
            {' · '}
            <FormattedMessage
              {...publishsetFormMessages.formatsSummaryPackages}
              values={{ count: packageCount }}
            />
          </>
        )}
      </div>
    </>
  );

  /**
   * Nabízí jen datové položky — cena sady se počítá z jejich pásem. Ceník je
   * dostupný pouze na IPR, jinde by select zůstal prázdný, proto se skrývá.
   */
  const canUsePriceList =
    customerType === 'ipr' && auth.hasPermission('PriceListItems', 'retrieve', null);

  const preparePriceListItems = useCallback(async () => {
    if (!canUsePriceList) return null;

    const response = await priceListItemsList({
      query: { type__in: DATA_PRICE_LIST_ITEM_TYPES },
    });
    if (response.ok) {
      return [
        { value: '', label: '' },
        ...(response.data?.results ?? []).map((priceListItem) => ({
          // Přehled ceníku hyperlink nevrací, skládá se proto z cesty generovaného klienta.
          value: `${globalThis.DMX_CONFIG.server}${priceListItemsRetrieveOperation({ id: priceListItem.id }).path}`,
          label: priceListItem.name,
        })),
      ];
    }
    return null;
  }, [canUsePriceList]);

  const price_list_item = useSelect<string, any>({
    name: 'price_list_item',
    label: formatMessage(publishsetFormMessages.priceListItem),
    initialValue: resource.data?.price_list_item?.url || '',
    onChange: onInputChangeCallback,
    prepareItems: preparePriceListItems,
    renderValue: (item) => item || '',
    renderItem: (item) => item,
    validate,
    ...formItemServerErrors.price_list_item,
  });

  // POST
  const formItems = {
    name,
    price_list_item,
    description,
    external_description_url,
    tags,
    attachments,
    state,
    datatype,
    data_type,
  };

  // HANDLERS
  const handleClose = useHistoryAction('vydejPublishsetList');
  const handleClear = useCallback(() => resource.refetch(), [resource]);
  const handleCancel = useCallback(() => resource.refetch(), [resource]);

  const handleDelete = async () => {
    if (window.confirm(formatMessage(publishsetFormMessages.publishsetAlertDelete))) {
      setServerErrors({});
      const deleteResult = await onDelete();
      setServerErrors(deleteResult.error?.data ?? {});
    }
  };

  const handleSubmit = async () => {
    setValidate(true);

    const values = getFormValues(formItems);

    const isSomeFormItemInvalid =
      (!acl.doNotRequireFormAndType && isFormatsEmpty) ||
      missingTileindexFormatUrls.length > 0 ||
      Object.values(formItems).some((formItem: any) => {
        return formItem.validator?.(formItem.value);
      });

    if (!isSomeFormItemInvalid) {
      submittedSelectionFormatUrlsRef.current = selectedTools.map((sel) => sel.format.url);
      const submitResult = await onSubmit({
        ...values,
        // Nevybraná položka znamená sadu zdarma.
        price_list_item: price_list_item.value || null,
        tags: values.tags.map((tag) => tag.url),
        attachments: values.attachments.length > 0 ? values.attachments : null,
        formats: formats.map((format) => format.url),
        export_tool_selections: selectedTools.map((sel) => ({
          format: sel.format.url,
          export_tool: sel.toolUrl,
          ...(sel.isPackage && { tileindex: tileindexSelections[sel.format.url] || null }),
        })),
        approver_roles,
        needs_approval: approver_roles.length > 0,
        publishset_datasets: publishsetDatasets
          .filter((publishsetDataset) => publishsetDataset?.dataset)
          .map((publishsetDataset) => ({
            id: publishsetDataset.id,
            dataset: publishsetDataset?.dataset.url,
            fields:
              publishsetDataset?.fields && publishsetDataset.fields.length > 0
                ? publishsetDataset.fields
                : null,
            condition: publishsetDataset.condition,
          })),
        ...(acl.updateAdminOnly && { admin_only: admin_only.isChecked }),
      });
      setServerErrors(submitResult.error?.data ?? {});
      dispatchPublishsetDatasetsAction({
        type: PUBLISHSET_DATASETS_ACTION_TYPE.SET_SERVER_ERRORS,
        errors: submitResult.error?.data?.publishset_datasets,
      });
      if (submitResult.ok) {
        setFormChanged(false);
        const publishsetsList = publishsetsListOperation(PUBLISHSET_LIST_FILTER);
        const publishsetTagsList = publishsetTagsListOperation(TAGS_LIST_FILTER);
        dispatch(actionResourceInvalidate(publishsetsList.path));
        dispatch(actionResourceInvalidate(publishsetTagsList.path));
      }
    }
  };

  return (
    <MainContent>
      <article className='sg-u-vs-4'>
        <header className='sg-a-d-f sg-a-ai-c sg-u-hsr-2'>
          <PageTitle>
            <FormattedMessage {...publishsetTitleMessages[formType]} />
          </PageTitle>
          {resource.data?.type && <FormatTypeBadge type={resource.data?.type} />}
        </header>
        {loadQueryState === QUERY_STATE.WAITING && (
          <WaitingAlert>
            <FormattedMessage {...publishsetQueryMessages.loadQueryStateWaiting} />
          </WaitingAlert>
        )}
        {loadQueryState === QUERY_STATE.FAIL && (
          <DangerAlert>
            <FormattedMessage {...publishsetQueryMessages.loadQueryStateFail} />
          </DangerAlert>
        )}
        {(loadQueryState === QUERY_STATE.SUCCESS || formType === PUBLISHSET_FORM_TYPE.NEW) && (
          <Form onSubmit={handleSubmit}>
            <div className='sg-u-vs-4'>
              <section>
                <FormBlock>
                  <FormRow>
                    <FormItem {...name}>
                      <Input {...name} />
                    </FormItem>
                  </FormRow>

                  <FormRow>
                    <FormItem {...state}>
                      <RadioGroup {...state} direction='row' />
                    </FormItem>
                  </FormRow>

                  {acl.updateAdminOnly && (
                    <FormRow>
                      <FormItem
                        label={formatMessage(publishsetFormMessages.visibility)}
                        errorMessage={admin_only.errorMessage}
                        help={formatMessage(publishsetFormMessages.adminOnlyHint)}
                        helpLabel={formatMessage(commonMessages.formItemHelpLabel)}
                      >
                        <Checkbox {...admin_only} isChecked={admin_only.isChecked}>
                          <FormattedMessage {...publishsetFormMessages.adminOnly} />
                        </Checkbox>
                      </FormItem>
                    </FormRow>
                  )}

                  {customerType === 'gp' && (
                    <FormRow>
                      <FormItem {...datatype}>
                        <RadioGroup {...datatype} direction='row' />
                      </FormItem>
                    </FormRow>
                  )}

                  {customerType === 'dtm' && (
                    <FormRow>
                      <FormItem {...data_type}>
                        <RadioGroup {...data_type} direction='row' />
                      </FormItem>
                    </FormRow>
                  )}

                  {canUsePriceList && (
                    <FormRow>
                      <FormItem
                        {...price_list_item}
                        help={formatMessage(publishsetFormMessages.priceListItemHint)}
                        helpLabel={formatMessage(commonMessages.formItemHelpLabel)}
                      >
                        <Select {...price_list_item} value={price_list_item.value ?? ''} />
                      </FormItem>
                    </FormRow>
                  )}

                  <FormRow>
                    <FormItem {...description}>
                      <Textarea {...description} />
                    </FormItem>
                  </FormRow>

                  <FormRow>
                    <FormItem {...external_description_url}>
                      <Input type='url' {...external_description_url} />
                    </FormItem>
                  </FormRow>

                  {!hideTags && (
                    <FormRow>
                      <FormItem {...tags}>
                        <MultiSelect {...tags} />
                      </FormItem>
                    </FormRow>
                  )}
                  <FormRow>
                    <FormItem {...attachments}>
                      <MultiSelect {...attachments} />
                    </FormItem>
                  </FormRow>

                  <FormRow>
                    <FormItem
                      label={formatMessage(publishsetFormMessages.approverRoles)}
                      {...formItemServerErrors.approver_roles}
                    >
                      <div className='sg-a-d-f sg-a-fw-w sg-u-hsr-2'>
                        <CheckboxGroup
                          direction='row'
                          isInvalid={formItemServerErrors.approver_roles?.isInvalid}
                          items={approverRoles.data?.results?.map((role) => role.url) ?? []}
                          renderLabel={(item) =>
                            approverRoles.data?.results?.find((role) => role.url === item)?.label ??
                            ''
                          }
                          values={approver_roles}
                          renderValue={(item) => item}
                          onChange={(e, eventData) => {
                            onInputChangeCallback();
                            setApproverRoles(eventData.values);
                          }}
                        />
                      </div>
                    </FormItem>
                  </FormRow>

                  {resource.data?.creator && (
                    <FormRow>
                      <FormItem label={formatMessage(publishsetFormMessages.creator)}>
                        {resource.data.creator?.name} {resource.data.creator?.surname}
                        <div className='sg-u-type-sml sg-u-c-body-darkest'>
                          <FormattedMessage
                            {...publishsetFormMessages.created}
                            values={{
                              created: resource.data.created
                                ? Date.parse(resource.data.created)
                                : '',
                            }}
                          />
                        </div>
                      </FormItem>
                      <FormItem label={formatMessage(publishsetFormMessages.editor)}>
                        {resource.data.editor?.name} {resource.data.editor?.surname}
                        <div className='sg-u-type-sml sg-u-c-body-darkest'>
                          <FormattedMessage
                            {...publishsetFormMessages.edited}
                            values={{
                              edited: resource.data.edited ? Date.parse(resource.data.edited) : '',
                            }}
                          />
                        </div>
                      </FormItem>
                    </FormRow>
                  )}
                </FormBlock>
              </section>

              <section>
                <h3 className='sg-u-type-h3 sg-a-ml-2 sg-a-mb-1'>
                  <FormattedMessage {...publishsetFormMessages.formats} />
                </h3>
                <FormBlock>
                  <FormRow>
                    <FormItem
                      label={formatMessage(publishsetFormMessages.type)}
                      isRequired={!acl.doNotRequireFormAndType}
                      help={
                        isTypeLocked ? undefined : formatMessage(publishsetFormMessages.typeHint)
                      }
                      helpLabel={formatMessage(commonMessages.formItemHelpLabel)}
                    >
                      {isTypeLocked ? (
                        <FormatTypeBadge type={selectedType} />
                      ) : (
                        <RadioBtnGroup
                          value={selectedType}
                          items={typeItems}
                          onChange={(e) =>
                            handleTypeChange(
                              (e.target as HTMLInputElement).value as PUBLISHSET_TYPES,
                            )
                          }
                        />
                      )}
                    </FormItem>
                  </FormRow>
                  <FormRow>
                    <FormItem
                      label={formatMessage(publishsetFormMessages.formats)}
                      isRequired={!acl.doNotRequireFormAndType}
                      errorMessage={formatsErrorMessage}
                    >
                      {availableFormats.data?.results && renderFormatCards()}
                    </FormItem>
                  </FormRow>
                </FormBlock>
              </section>

              <section>
                <h3 className='sg-u-type-h3 sg-a-ml-2 sg-a-mb-1'>
                  <FormattedMessage {...publishsetFormMessages.publishsetDatasetList} />
                </h3>
                <FormBlock>
                  {publishsetDatasets.length > 0 && (
                    <div className='dmx-publishetDatasetTable'>
                      <div className='dmx-publishetDatasetColumnName'>
                        <FormattedMessage {...publishsetFormMessages.datasetName} />
                      </div>
                      <div className='dmx-publishetDatasetColumnName'>
                        <FormattedMessage {...publishsetFormMessages.datasetFields} />
                      </div>
                      <div className='dmx-publishetDatasetColumnName'>
                        <FormattedMessage {...publishsetFormMessages.datasetCondition} />
                      </div>
                      <div className='sg-a-w-1px' />
                      {publishsetDatasets.map((publishsetDataset) => (
                        <PublishsetDatasetListItem
                          key={publishsetDataset.uid ?? publishsetDataset.id}
                          uid={(publishsetDataset.uid ?? publishsetDataset.id) as number}
                          onChange={onDatasetChange}
                          onDelete={onDatasetDelete}
                          publishsetDataset={publishsetDataset}
                        />
                      ))}
                    </div>
                  )}
                  <BtnBar align='left'>
                    <SecondaryBtn onClick={handleNewPublishsetDataset}>
                      <FormattedMessage {...publishsetBtnMessages.newPublishsetDataset} />
                    </SecondaryBtn>
                  </BtnBar>
                </FormBlock>
              </section>

              {submitQueryState === QUERY_STATE.WAITING && (
                <WaitingAlert>
                  <FormattedMessage {...publishsetQueryMessages.submitQueryStateWaiting} />
                </WaitingAlert>
              )}
              {submitQueryState === QUERY_STATE.FAIL && (
                <DangerAlert>
                  <FormattedMessage {...publishsetFailMessages[formType]} />
                </DangerAlert>
              )}
              {submitQueryState === QUERY_STATE.SUCCESS && (
                <SuccessAlert>
                  <FormattedMessage {...publishsetQueryMessages.submitQueryStateSuccess} />
                </SuccessAlert>
              )}

              {formItemServerErrors.non_field_errors && (
                <DangerAlert>{formItemServerErrors.non_field_errors.errorMessage!}</DangerAlert>
              )}

              {deleteQueryState === QUERY_STATE.WAITING && (
                <WaitingAlert>
                  <FormattedMessage {...publishsetQueryMessages.deleteQueryStateWaiting} />
                </WaitingAlert>
              )}
              {deleteQueryState === QUERY_STATE.FAIL && (
                <DangerAlert>
                  <FormattedMessage {...publishsetQueryMessages.deleteQueryStateFail} />
                </DangerAlert>
              )}

              <BtnBar>
                <>
                  {formType === PUBLISHSET_FORM_TYPE.EDIT && (
                    <>
                      {isFormChanged ? (
                        <>
                          <CtaBtn type='submit'>
                            <FormattedMessage {...publishsetBtnMessages.save} />
                          </CtaBtn>
                          <SecondaryBtn isDanger onClick={handleCancel}>
                            <FormattedMessage {...publishsetBtnMessages.cancel} />
                          </SecondaryBtn>
                        </>
                      ) : (
                        <CtaBtn onClick={handleClose}>
                          <FormattedMessage {...publishsetBtnMessages.undo} />
                        </CtaBtn>
                      )}
                      {auth.hasPermission('Publishsets', 'destroy', resource?.data) && (
                        <SecondaryBtn isDanger onClick={handleDelete}>
                          <FormattedMessage {...publishsetBtnMessages.delete} />
                        </SecondaryBtn>
                      )}
                    </>
                  )}
                  {formType === PUBLISHSET_FORM_TYPE.NEW && (
                    <>
                      <CtaBtn type='submit'>
                        <FormattedMessage {...publishsetBtnMessages.create} />
                      </CtaBtn>
                      <SecondaryBtn isDanger onClick={handleClear}>
                        <FormattedMessage {...publishsetBtnMessages.clear} />
                      </SecondaryBtn>
                    </>
                  )}
                </>
              </BtnBar>
            </div>
          </Form>
        )}
      </article>
    </MainContent>
  );
};
